import { BaseService } from "./BaseService";
import { ErrorResponse, SuccessResponse, BuildxConfig } from "../types/index";

/**
 * Templates service for Buildx
 * Handles template rendering and preview
 * 
 * @example
 * ```typescript
 * const templates = buildx.templates();
 * 
 * // Preview a template
 * const preview = await templates.preview('template', { name: 'John' });
 * 
 * // Render a template
 * const rendered = await templates.render('template-id', { name: 'John' });
 * 
 * // Generate PDF
 * await templates.generatePDF('template-id', data);
 * ```
 */
export class Templates {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * Preview a template with data
	 */
	async preview(template: any, data: any, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/templates/preview");
		return await this.baseService.post(url, { template, data });
	}

	/**
	 * Render a template by ID with data
	 */
	async render(templateId: string, data: any, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/templates/${templateId}/render`);
		return await this.baseService.post(url, data);
	}

	/**
	 * Render a template as PDF and download (browser only)
	 */
	async renderPDF(templateId: string, data: any, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/templates/${templateId}/pdf`);
		const headers = { ...(await this.baseService.getHeaders()), responseType: "blob" };
		const response = await this.baseService.axiosInstance.post(url, data, { headers });

		// Create download link for browser
		if (typeof window !== "undefined") {
			const blobUrl = window.URL.createObjectURL(response.data);
			const link = document.createElement("a");
			link.href = blobUrl;
			link.setAttribute("download", "document.pdf");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl);
		}

		return { success: true, message: "PDF downloaded successfully" };

	}
} 